import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SyncBadge } from "@/components/common/SyncBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES, LANGUAGES, dt, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function Agents() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [draft, setDraft] = useState({
    name: "", company_name: "Nova Solutions", language: "sq", country: "AL",
    product: "", voice_id: "voice_elena_sq", tone: "professional",
    voice_agent_provider: "vapi", phone_number_id: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["agents", page, q],
    queryFn: () => get("/agents", { page, page_size: 20, q: q || undefined }),
  });
  const { data: voices } = useQuery({ queryKey: ["voices"], queryFn: () => get("/voices") });
  const { data: numbers } = useQuery({
    queryKey: ["agent-free-numbers"],
    queryFn: () => get("/phone-numbers", { page_size: 200 }),
  });
  const { data: allAgents } = useQuery({
    queryKey: ["agents", "number-owners"],
    queryFn: () => get("/agents", { page_size: 200 }),
  });
  const assigned = new Set((allAgents?.items || []).map((a) => a.phone_number_id).filter(Boolean));
  const freeNumbers = (Array.isArray(numbers) ? numbers : numbers?.items || []).filter(
    (n) => n.status === "active" && !assigned.has(n.id)
  );

  const create = useMutation({
    mutationFn: () =>
      api.post("/agents", {
        ...draft,
        phone_number_id: draft.phone_number_id || null,
        role: "Outbound sales agent",
        conversation: {
          first_message: `Hello, this is ${draft.name} from ${draft.company_name}. Do you have a moment?`,
          system_prompt: `You are ${draft.name}, an outbound sales agent for ${draft.company_name} selling ${draft.product || "our service"}.\nState clearly at the start of the call that you are an AI assistant and confirm it honestly whenever the person asks.\nObjective: qualify the prospect for ${draft.product || "our service"} and agree a clear next step.\nIf the person asks not to be contacted again, confirm it, stop selling and end the call politely.`,
        },
        knowledge: {},
      }),
    onSuccess: ({ data: created }) => {
      toast.success("Agent draft created");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      navigate(`/agents/${created.id}`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/agents/${id}`),
    onSuccess: () => {
      toast.success("Agent deleted");
      setRemoving(null);
      queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <>
      <PageHeader
        testId="agents-page"
        title="AI agents"
        subtitle="Voice, prompt, knowledge base and immutable published versions"
        actions={
          can("agents", "write") ? (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-agent-button">
                  New agent
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader>
                  <DialogTitle>Create AI agent</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Agent name</Label>
                    <Input data-testid="agent-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Company</Label>
                    <Input data-testid="agent-company-input" value={draft.company_name} onChange={(e) => setDraft({ ...draft, company_name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Product</Label>
                    <Input data-testid="agent-product-input" value={draft.product} onChange={(e) => setDraft({ ...draft, product: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Language</Label>
                    <Select value={draft.language} onValueChange={(v) => setDraft({ ...draft, language: v })}>
                      <SelectTrigger data-testid="agent-language-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Country</Label>
                    <Select value={draft.country} onValueChange={(v) => setDraft({ ...draft, country: v })}>
                      <SelectTrigger data-testid="agent-country-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {COUNTRIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Voice</Label>
                    <Select value={draft.voice_id} onValueChange={(v) => setDraft({ ...draft, voice_id: v })}>
                      <SelectTrigger data-testid="agent-voice-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {(voices || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Voice agent provider</Label>
                    <Select value={draft.voice_agent_provider} onValueChange={(v) => setDraft({ ...draft, voice_agent_provider: v })}>
                      <SelectTrigger data-testid="agent-provider-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="vapi">Vapi (live calls)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label>Outbound number</Label>
                    <Select value={draft.phone_number_id} onValueChange={(v) => setDraft({ ...draft, phone_number_id: v })}>
                      <SelectTrigger data-testid="agent-number-select">
                        <SelectValue placeholder={freeNumbers.length ? "Select a free number" : "No free number available"} />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {freeNumbers.map((n) => (
                          <SelectItem key={n.id} value={n.id}>
                            {n.phone} · {n.country}{n.voice_provider_resource_id ? " · in Vapi" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      Each agent calls from its own number. Live Vapi agents also need the number
                      imported to Vapi; numbers used by another agent are not listed.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={!draft.name || create.isPending}
                    onClick={() => create.mutate()}
                    data-testid="agent-create-submit"
                  >
                    Create draft
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : null
        }
      />

      <Section
        testId="agents-section"
        title={`${data?.total ?? 0} agents`}
        actions={
          <Input
            data-testid="agents-search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder="Search agents"
            className="h-9 w-48"
          />
        }
      >
        <DataTable
          testId="agents-table"
          rowTestId="agent-row"
          loading={isLoading}
          onRowClick={(r) => navigate(`/agents/${r.id}`)}
          columns={[
            { key: "name", label: "Agent", render: (r) => <span className="font-medium text-slate-800">{r.name}</span> },
            { key: "company_name", label: "Company" },
            { key: "language", label: "Language", render: (r) => (r.language || "").toUpperCase() },
            { key: "country", label: "Country" },
            { key: "product", label: "Product", render: (r) => r.product || "—" },
            { key: "voice_id", label: "Voice", render: (r) => <span className="font-mono text-xs">{r.voice_id}</span> },
            { key: "tone", label: "Tone", render: (r) => title(r.tone) },
            { key: "published_version", label: "Published", render: (r) => (r.published_version ? `v${r.published_version}` : "—") },
            { key: "version_count", label: "Versions" },
            { key: "campaign_count", label: "Campaigns" },
            {
              key: "status",
              label: "Status",
              render: (r) => <StatusBadge value={r.draft_dirty ? "draft" : r.status} label={r.draft_dirty ? "Unpublished changes" : title(r.status)} />,
            },
            {
              key: "sync_status",
              label: "Provider sync",
              render: (r) => <SyncBadge status={r.sync_status || "not_connected"} testId={`agent-sync-${r.id}`} />,
            },
            { key: "phone_number", label: "Number", render: (r) => (
              <span data-testid={`agent-number-${r.id}`} className="font-mono text-xs">
                {r.phone_number || <span className="text-amber-600">not assigned</span>}
              </span>
            ) },
            { key: "updated_at", label: "Updated", render: (r) => dt(r.updated_at) },
            ...(can("agents", "write") ? [{
              key: "actions",
              label: "",
              render: (r) => (
                <div onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                    data-testid={`agent-delete-${r.id}`}
                    onClick={() => setRemoving(r)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ),
            }] : []),
          ]}
          rows={data?.items || []}
          empty={{ title: "No agents yet", description: "Create an AI agent to configure voice, prompt and knowledge." }}
        />
        <Pagination page={data?.page || 1} pages={data?.pages || 1} total={data?.total || 0} onChange={setPage} />
      </Section>

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        destructive
        title={`Delete ${removing?.name || "this agent"}?`}
        description="The agent is removed from the list. An agent used by a running campaign cannot be deleted until that campaign is stopped."
        confirmLabel="Delete agent"
        onConfirm={() => remove.mutateAsync(removing.id)}
        testId="agent-delete-dialog"
      />
    </>
  );
}